/**
 * ReceivablePayableWidget — what you're owed against what you owe.
 *
 * COST: ZERO extra Firestore reads. Reads the same scoreboard document the KPI
 * cards already load (src/utils/scoreboardData.js).
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiPieChart } from 'react-icons/fi';
import { useReducedMotion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import { getScoreboard, scoreboardUsable, scoreboardTotals } from '../../utils/scoreboardData';

const RECEIVABLE = '#F59E0B';
const PAYABLE = '#8B5CF6';

const R = 62;              // donut radius
const STROKE = 22;
const C = 2 * Math.PI * R; // circumference

const ReceivablePayableWidget = () => {
  const prefersReduced = useReducedMotion();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  // Sweep the ring in once, after the real figures arrive. Starting at 0 and
  // flipping to the true length on the next frame is what makes it animate —
  // and gating on `totals` means it never sweeps to a placeholder and jumps.
  const [swept, setSwept] = useState(false);
  const companyId = activeCompany?.id;
  const fyId = activeFinancialYear?.id || null;
  const branchId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;

  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!companyId || !fyId) { setLoading(false); setTotals(null); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const board = await getScoreboard(companyId);
        if (cancelled) return;
        // scoreboardTotals returns null for a branch with no node — deliberately
        // treated as "no data" rather than four zeros, which would read as real.
        setTotals(scoreboardUsable(board, fyId) ? scoreboardTotals(board, fyId, branchId) : null);
      } catch (e) {
        if (!cancelled) setTotals(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, fyId, branchId]);

  // Trigger the sweep only once the real totals exist — never animate toward a
  // placeholder, or the ring grows and then jumps when the data replaces it.
  useEffect(() => {
    if (!totals) { setSwept(false); return undefined; }
    const id = requestAnimationFrame(() => setSwept(true));
    return () => cancelAnimationFrame(id);
  }, [totals]);

  if (!companyId) return null;

  const receivable = Math.max(0, totals?.receivable || 0);
  const payable = Math.max(0, totals?.payable || 0);
  const sum = receivable + payable;
  const hasData = !!totals && sum > 0;

  const net = receivable - payable;
  const recvLen = hasData ? (receivable / sum) * C : 0;
  // Grow to the real length one frame after the data lands (or immediately if
  // motion is reduced).
  const drawn = prefersReduced || swept;

  return (
    <Card>
      <Header>
        <HeaderLeft>
          <IconWrap><FiPieChart size={15} /></IconWrap>
          <div>
            <Title>Owed to you vs owed by you</Title>
            <Sub>Receivable against payable</Sub>
          </div>
        </HeaderLeft>
      </Header>

      {loading ? (
        <Skeleton />
      ) : !hasData ? (
        <EmptyBox>Nothing outstanding either way.</EmptyBox>
      ) : (
        <>
          <ChartWrap>
            <svg viewBox="0 0 230 190" width="100%" height="196" role="img"
                 aria-label={`Receivable ${formatCurrency(receivable)}, payable ${formatCurrency(payable)}`}>
              <g transform="translate(115,95)">
                <circle r={R} fill="none" stroke="var(--color-border-light)" strokeWidth={STROKE} />
                {/* payable fills the ring, receivable is drawn over it */}
                <circle r={R} fill="none" stroke={PAYABLE} strokeWidth={STROKE}
                        transform="rotate(-90)" strokeLinecap="round"
                        strokeDasharray={`${drawn ? C : 0} ${C}`}
                        style={{ transition: prefersReduced ? 'none' : 'stroke-dasharray 700ms cubic-bezier(.22,.61,.36,1)' }} />
                <circle r={R} fill="none" stroke={RECEIVABLE} strokeWidth={STROKE}
                        transform="rotate(-90)" strokeLinecap="round"
                        strokeDasharray={`${drawn ? recvLen : 0} ${C}`}
                        style={{ transition: prefersReduced ? 'none' : 'stroke-dasharray 700ms cubic-bezier(.22,.61,.36,1) 80ms' }} />
                <text textAnchor="middle" y="-3" fontSize="18" fontWeight="800"
                      fill={net >= 0 ? 'var(--color-success-main, #0f9d58)' : 'var(--color-error-main, #d93025)'}>
                  {formatCurrency(net)}
                </text>
                <text textAnchor="middle" y="15" fontSize="10" fill="var(--color-text-tertiary)">
                  net position
                </text>
              </g>
            </svg>
          </ChartWrap>
          <Legend>
            <LegendItem>
              <Swatch $c={RECEIVABLE} />
              <span>Receivable</span>
              <b>{formatCurrency(receivable)}</b>
            </LegendItem>
            <LegendItem>
              <Swatch $c={PAYABLE} />
              <span>Payable</span>
              <b>{formatCurrency(payable)}</b>
            </LegendItem>
          </Legend>
        </>
      )}
    </Card>
  );
};

export default ReceivablePayableWidget;

/* ───────────── styles ───────────── */
const Card = styled.div`
  background: var(--color-background-default);
  border: 1px solid var(--color-border-light);
  border-radius: 14px;
  padding: 16px;
  height: 100%;
`;
const Header = styled.div`display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;`;
const HeaderLeft = styled.div`display:flex;align-items:center;gap:10px;`;
const IconWrap = styled.div`
  width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;
  background:${RECEIVABLE}18;color:#b06f00;
`;
const Title = styled.div`font-size:13.5px;font-weight:700;color:var(--color-text-primary);`;
const Sub = styled.div`font-size:11px;color:var(--color-text-tertiary);margin-top:2px;`;
const ChartWrap = styled.div`display:flex;justify-content:center;`;
const Legend = styled.div`display:flex;flex-direction:column;gap:7px;margin-top:6px;`;
const LegendItem = styled.div`
  display:flex;align-items:center;gap:8px;font-size:12px;color:var(--color-text-secondary);
  b{margin-left:auto;color:var(--color-text-primary);font-weight:700;}
`;
const Swatch = styled.i`width:10px;height:10px;border-radius:3px;background:${p => p.$c};display:inline-block;`;
const EmptyBox = styled.div`padding:44px 16px;text-align:center;font-size:12.5px;color:var(--color-text-tertiary);`;
const Skeleton = styled.div`
  height:240px;border-radius:10px;
  background:linear-gradient(90deg,var(--color-border-light) 25%,transparent 37%,var(--color-border-light) 63%);
  background-size:400% 100%;animation:rpshimmer 1.4s ease infinite;
  @keyframes rpshimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}
`;
